/* HeroLanding.jsx -- fleet standard #1: the full-screen motion hero.
 *
 * Ported from ~/.claude/skills/scaffold-app/templates/HeroLanding.tsx to this
 * app's CRA/JSX setup -- types stripped, every comment and every behaviour kept,
 * because each of those comments is a defect somebody already paid for.
 *
 * AegisOps had NO landing page: `/` was the protected console, so an
 * unauthenticated visitor was bounced straight to /login and the product had no
 * front door at all. Brand tokens live at the top of hero.css.
 *
 * WHY IT IS BUILT THIS WAY (each line is a defect already paid for):
 *
 *  - Ambient loops are CSS keyframes, not a JS animation library. CSS runs off
 *    the main thread and keeps moving while the page is still hydrating; a JS
 *    tween on a hero that nobody interrupts earns nothing and stalls behind
 *    hydration on the mid-range Android this fleet actually serves.
 *  - Depth comes from soft radial gradients, NEVER `filter: blur()`. A blur on
 *    three full-screen elements is a per-frame cost on exactly those handsets.
 *  - Field geometry is computed ONCE from a seeded PRNG into CSS custom
 *    properties. Same layout every load, no per-frame JS, no layout thrash.
 *  - Reveal delays ride a `--d` custom property, never an inline
 *    `animation-delay`. Inline styles beat any stylesheet rule, so with the
 *    delay set directly a reduced-motion visitor still sits through the full
 *    sequence -- the media block compresses the ORDER instead of deleting it.
 *  - Pointer parallax is skipped on coarse pointers and under reduced motion,
 *    and is applied to a wrapper so it composes with the ambient transforms
 *    instead of fighting them.
 */
import { useEffect, useMemo, useRef } from 'react'
import './hero.css'

/* ------------------------------------------------------------------ tunables */
const MOTES = 14          // ambient particles. Above ~20 the field reads as noise.
const SEED = 20260905     // change to redraw the field; keep it fixed per brand.
const STAGGER_MS = 180    // premium reads SLOW. 30-80ms reads as an app booting.
const FIRST_MS = 320      // when the first element arrives.

/* Deterministic PRNG (mulberry32). A random field that moves between loads
 * makes a "did the design change?" question unanswerable. */
function prng(seed) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export default function HeroLanding({ eyebrow, title, lede, primaryCta, secondaryCta }) {
  const stageRef = useRef(null)

  /* Geometry once, into custom properties. */
  const motes = useMemo(() => {
    const rand = prng(SEED)
    return Array.from({ length: MOTES }, () => ({
      '--x': `${(rand() * 100).toFixed(2)}%`,
      '--y': `${(rand() * 100).toFixed(2)}%`,
      '--size': `${(1.5 + rand() * 3.5).toFixed(2)}px`,
      '--drift': `${(-40 + rand() * 80).toFixed(1)}px`,
      '--rise': `${(-70 - rand() * 90).toFixed(1)}px`,
      // 30-52s. Long, `alternate`, and NO negative delay: a mote never leaves
      // frame, so a wrapped restart would snap it across the screen.
      '--dur': `${(30 + rand() * 22).toFixed(1)}s`,
      '--delay': `${(rand() * 6).toFixed(1)}s`,
      '--dim': (0.18 + rand() * 0.3).toFixed(2),
    }))
  }, [])

  /* Pointer parallax -- desktop, fine pointer, motion allowed. */
  useEffect(() => {
    const stage = stageRef.current
    if (!stage) return
    const fine = window.matchMedia('(pointer: fine)').matches
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (!fine || still) return

    let frame = 0
    const onMove = (e) => {
      if (frame) return
      frame = requestAnimationFrame(() => {
        frame = 0
        const dx = e.clientX / window.innerWidth - 0.5
        const dy = e.clientY / window.innerHeight - 0.5
        stage.style.setProperty('--px', `${(dx * 18).toFixed(2)}px`)
        stage.style.setProperty('--py', `${(dy * 12).toFixed(2)}px`)
      })
    }
    window.addEventListener('pointermove', onMove, { passive: true })
    return () => {
      window.removeEventListener('pointermove', onMove)
      if (frame) cancelAnimationFrame(frame)
    }
  }, [])

  const d = (i) => ({ '--d': `${FIRST_MS + i * STAGGER_MS}ms` })

  return (
    <section className="hero" ref={stageRef} aria-label={title}>
      {/* --- motion field. aria-hidden: decorative, and a screen reader
              announcing 14 empty divs is worse than silence. --- */}
      <div className="hero-field" aria-hidden="true">
        <div className="hero-ground" />
        <div className="hero-aurora hero-aurora-a" />
        <div className="hero-aurora hero-aurora-b" />
        <div className="hero-pool" />
        <div className="hero-mesh" />
        <div className="hero-sheen" />
        {motes.map((style, i) => (
          <span className="hero-mote" key={i} style={style} />
        ))}
        {/* Scrim last: every headline must clear 4.5:1 against the field at
            its BRIGHTEST frame, not against the still. */}
        <div className="hero-scrim" />
      </div>

      <div className="hero-copy">
        <p className="hero-eyebrow" style={d(0)}>{eyebrow}</p>
        <h1 className="hero-title" style={d(1)}>{title}</h1>
        <div className="hero-rule" style={d(2)} />
        <p className="hero-lede" style={d(3)}>{lede}</p>
        <div className="hero-actions" style={d(4)}>
          <a className="hero-cta" href={primaryCta.href}>{primaryCta.label}</a>
          {secondaryCta && (
            <a className="hero-cta hero-cta-ghost" href={secondaryCta.href}>
              {secondaryCta.label}
            </a>
          )}
        </div>
      </div>

      <div className="hero-scroll" style={d(5)} aria-hidden="true">
        <span className="hero-scroll-line" />
      </div>
    </section>
  )
}
